import React, { useEffect, useMemo, useState } from 'react';
import { Guide } from '@/pages/home/components/Guide';
import { getLocale, useSelector, useDispatch, useLocation, history, useIntl } from 'umi';
import { storage } from '@/utils/utils'
import { LANGUAGE_CN } from '@/pages/home/_config/config';
import { GUIDE_SYS_DASHBOARD, GUIDE_TYPE_DASHBOARD_COUNTRY } from '@/pages/home/models/guide';
import { DASHBOARD_GUIDE_BOX_LIST3, DASHBOARD_GUIDE_HIDE, DASHBOARD_SHOW_GUIDE_GAME_LIST } from '@/pages/home/components/GuideModule/GuideDashboardBox';

import guideImg_3_1_cn from '@/pages/dashboard/assets/images/guide/cn/guide-dbd-feature3-1.png';
import guideImg_3_1_en from '@/pages/dashboard/assets/images/guide/en/guide-dbd-feature3-1.png';
import guideImg_3_2_cn from '@/pages/dashboard/assets/images/guide/cn/guide-dbd-feature3-2.png';
import guideImg_3_2_en from '@/pages/dashboard/assets/images/guide/en/guide-dbd-feature3-2.png';

import './index.scss';

const STEP1_PATH = '/dashboard/game/demo/comparisonAnalysis/countryComparison';
const STEP2_PATH = '/dashboard/game/demo/multiTimezone/dailyTimezone/country';
const STEP_PATH_LIST = [STEP1_PATH, STEP2_PATH]

const GuideCountryComparison = (props: any) => {
  const locale = getLocale();
  const { formatMessage } = useIntl();
  const isEn = locale === 'en-US';
  const dispatch = useDispatch();
  const location = useLocation();
  const { pathname, query } = location;

  const { from } = query || {}
  const [currentGuideStep, setCurrentGuideStep] = useState(-3);
  const { guideType, userInfo } = useSelector((state: any) => {
    const { guide: { guideType }, home: { userInfo } } = state;
    return {
      guideType,
      userInfo
    }
  });

  // 注意下面两个useEffect不要合并到一起，不然会出现：从头部导航进入新手引导，点击到下一步时，关闭按钮无法关闭，而是返回到第一步
  useEffect(() => {
    if (guideType === GUIDE_TYPE_DASHBOARD_COUNTRY) {
      if (pathname !== STEP1_PATH && from !== 'userGuid_1') {
        return;
      }
      setCurrentGuideStep(from === 'userGuid_1' ? 1 : 0);
    }
  }, [guideType]);

  useEffect(() => {
    const list = storage.getLocalStorageItem(DASHBOARD_GUIDE_BOX_LIST3) || [];
    const guideHide = storage.getLocalStorageItem(DASHBOARD_GUIDE_HIDE);
    if (userInfo?.openid && !list.includes(userInfo.openid) && !guideHide) {
      if (pathname !== STEP1_PATH && from !== 'userGuid_1') {
        return;
      }
      setCurrentGuideStep(from === 'userGuid_1' ? 1 : 0);
    }
  }, [userInfo])

  const steps = useMemo(() => {
    return [
      {
        highlightElements: [{
          element: document.getElementsByClassName('menu-country-comparison')?.[0]
        }],
        element: () =>
        document.querySelector('.dbd-country-comparison .js-search-items-country'),
        title: formatMessage({ id: 'guide_country_comparison_step1_title' }),
        subtitle: formatMessage({ id: 'guide_country_comparison_step1_subtitle' }),
        description: formatMessage({ id: 'guide_country_comparison_step1_description' }),
        placement: 'left-end',
        bubbleClassName:'guide-country-comparison-bubble',
        bubbleStyle: { bottom: '-58px' },
        extImage: {
          image: isEn ? guideImg_3_1_en : guideImg_3_1_cn,
          placement: 'bottom-end',
          style: { width: '541px' },
        },
      },
      {
        highlightElements: [{
          element: document.getElementsByClassName('menu-daily-time-country')?.[0]
        }],
        element: () =>
          document.querySelector('.dbd-daily-timezone-zone .js-search-items-country'),
        title: formatMessage({ id: 'guide_country_comparison_step2_title' }),
        subtitle: formatMessage({ id: 'guide_country_comparison_step2_subtitle' }),
        description: formatMessage({ id: 'guide_country_comparison_step2_description' }),
        placement: 'left-start',
        extImage: {
          image: isEn ? guideImg_3_2_en : guideImg_3_2_cn,
          placement: 'bottom-start',
          style: { width: '541px' },
        },
        scrollIntoViewOptions: {
          behavior: 'auto',
          block: 'start',
          inline: 'center',
        }
      },
    ];
  }, [currentGuideStep]);

  const onStepChange = (current: number) => {
    const { pathname } = location;
    if (current >= 0 && pathname !== STEP_PATH_LIST[current]) {
      history.push(`${STEP_PATH_LIST[current]}?from=userGuid_${current}`);
    }
    setCurrentGuideStep(current);
  }

  // 流程完成后保存至local storage
  const updateGuideCompletionStatus = () => {
    if (userInfo.openid) {
      const list = storage.getLocalStorageItem(DASHBOARD_GUIDE_BOX_LIST3) || [];
      !list?.includes(userInfo.openid) && list.push(userInfo.openid);
      storage.setLocalStorageItem(DASHBOARD_GUIDE_BOX_LIST3, list);
    }
  };

  const onCloseStep = () => {
    updateGuideCompletionStatus();

    dispatch({
      type: 'guide/save',
      payload: {
        guideSys: GUIDE_SYS_DASHBOARD,
        guideType: '',
      },
    });
  }

  const onFinished = () => {
    updateGuideCompletionStatus();

    dispatch({
      type: 'guide/showDashboardGuideModule',
      payload: true,
    });
  }
  return (
    // finishText 根据中英文调整
    <Guide
      current={currentGuideStep}
      onCurrentChange={(
        current: number,
        context: { from: 'start' | 'back' | 'next' | 'finish' | 'cancel' },
      ) => {
        const { from } = context;
        onStepChange(current);
        if (from === 'finish') {
          onFinished();
        } else if (from === 'cancel') {
          onCloseStep();
        }
      }}
      visibleCloseBtn
      hideCancelButton
      showBackButton
      nextButtonTheme="primary"
      backText={formatMessage({ id: 'guide_last_step' })}
      nextText={formatMessage({ id: 'guide_next_step' })}
      finishText={formatMessage({ id: 'guide_more_step' })}
      steps={steps}
    />
  );
};

export default () => {
  const { currentGame } = useSelector(state => {
    // @ts-ignore
    const { currentGame } = state.dashboard;
    return {
      currentGame
    }
  });

  return <>
    {currentGame && DASHBOARD_SHOW_GUIDE_GAME_LIST.includes(currentGame.game_code) && <GuideCountryComparison />}
  </>
}
